import { useEffect, useMemo, useState } from "react";
import { Drawer } from "../Drawer";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { trpc } from "../../lib/trpc";

interface PickedProduct {
  itemKind: "semi" | "finished";
  itemId: string;
  itemLabel: string;
  uom: string;
}

interface Props {
  open: boolean;
  centerId: string | null;
  centerName?: string;
  onPick: (p: PickedProduct) => void;
  onClose: () => void;
}

/**
 * Stoğa üretim — ürün seçici. Merkeze atanmış aktif reçetelerin ürünlerini listeler;
 * seçilince çağıran taraf ProductionBatchDrawer'ı (sipariş-siz, merkez bağlamlı) açar.
 */
export function StockProductionPicker({ open, centerId, centerName, onPick, onClose }: Props) {
  const [sel, setSel] = useState("");
  const prodQ = trpc.productionBatch.producibleForCenter.useQuery(
    { centerId: centerId ?? "" },
    { enabled: open && !!centerId, staleTime: 30_000 },
  );
  const items = (prodQ.data ?? []) as PickedProduct[];

  useEffect(() => { if (open) setSel(""); }, [open]);

  const options = useMemo<SSOption[]>(
    () => items.map((i) => ({ value: i.itemId, label: i.itemLabel, tail: i.uom })),
    [items],
  );

  function go() {
    const it = items.find((i) => i.itemId === sel);
    if (it) onPick(it);
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="sm"
      title="Stoğa Üretim — Ürün Seç"
      subtitle={centerName ? `Merkez: ${centerName}` : undefined}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose}>İptal</button>
          <button type="button" className="btn-md primary" onClick={go} disabled={!sel}>Devam</button>
        </>
      }
    >
      <div className="form-field">
        <label className="lbl" htmlFor="sp-item">Üretilecek Ürün <span className="req">*</span></label>
        <SearchSelect
          id="sp-item"
          value={sel || null}
          options={options}
          onChange={(v) => setSel(v ?? "")}
          placeholder={prodQ.isLoading ? "Yükleniyor…" : "— Ürün seç —"}
          searchPlaceholder="Ürün adı ara…"
        />
        {prodQ.isSuccess && items.length === 0 ? (
          <span className="help" style={{ color: "var(--warning-text)" }}>
            Bu merkeze atanmış aktif reçete yok. Reçetelerde "üretim merkezi" olarak bu merkezi seçin.
          </span>
        ) : (
          <span className="help">Bu merkeze atanmış aktif reçetelerin ürünleri. Seçip "Devam" deyince miktar ve parti sayısı girilir; bileşenler FEFO ile düşülür.</span>
        )}
      </div>
    </Drawer>
  );
}
